import { useMemo, useState } from "react";
import { toast } from "sonner";
import type { ColumnDef } from "@tanstack/react-table";

import { MoreHorizontal } from "lucide-react";

import { DataTable } from "@/components/DataTable";
import { Pagination } from "@/components/Pagination";
import { Checkbox } from "@/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import { formatVND } from "@/lib/format";
import { cn } from "@/lib/utils";
import { useBranchLabel } from "@/lib/branches";
import { useProductLabel } from "@/features/products/productLabelContext";
import { useAuthV2 } from "@/lib/authv2/store";
import { Badge } from "@/ui/badge";
import { Button } from "@/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/ui/dialog";
import { Skeleton } from "@/ui/skeleton";
import { Textarea } from "@/ui/textarea";

import { canWriteAftercare } from "../aftercarePermissions";
import {
  AFTERCARE_CHANNELS,
  AFTERCARE_CLOSE_REASONS,
  AFTERCARE_FILTERS,
  useAftercareClose,
  useAftercareEmail,
  useAftercareList,
  useAftercareReopen,
  useAftercareTouch,
  type AftercareChannelId,
  type AftercareCloseReasonId,
  type AftercareFilterId,
  type AftercareItem,
} from "../aftercareApi";
import { HintNote } from "@/components/HintTip";

// Sổ hậu mãi (spec §6, quyết định 0043) — danh sách đơn đã trả ảnh cần chào
// mua thêm. Màn này KHÔNG tự nhắn cho khách và KHÔNG có bộ hẹn giờ nào: mọi
// dòng chỉ đổi khi có người bấm nút. Đếm lần chào, chặn quá 3 lần, tự đóng khi
// chạm trần đều là việc của máy chủ — ở đây chỉ hiện lại kết quả.

const PAGE_SIZE = 20;

/**
 * Ba nhóm cấp bách, dùng CÙNG hai mốc 3/7 ngày mà máy chủ dùng để sắp xếp
 * (aftercare/where.go urgencyBucketExpr) và cùng mốc màu của spec §6.4.
 *
 * Vì sao chia nhóm trên màn: máy chủ trả về đúng thứ tự rồi, nhưng một danh
 * sách 20 dòng phẳng thì không ai nhìn ra chỗ nào là "phải gọi hôm nay" và
 * chỗ nào là "để mai cũng được". Tiêu đề nhóm nói thẳng ranh giới đó.
 *
 * Ba mảnh này phải khớp với urgencyBucketExpr ở máy chủ. Lệch nhau thì màn
 * hình dựng tiêu đề nhóm không đúng chỗ dòng thật sự nằm.
 */
const URGENCY_GROUPS = [
  { id: "qua7", label: "QUÁ 7 NGÀY", hint: "nên gọi hậu mãi", min: 7, tone: "text-red-700" },
  { id: "qua3", label: "QUÁ 3 NGÀY", hint: "nên chào in ảnh", min: 3, tone: "text-amber-700" },
  { id: "moi", label: "MỚI TRẢ ẢNH", hint: "chưa tới hạn chào", min: 0, tone: "text-foreground" },
] as const;

const groupOf = (days: number) => URGENCY_GROUPS.find((g) => days >= g.min) ?? URGENCY_GROUPS[2];

type Props = {
  /** Mở form tạo đơn in cho đơn này (do trang cha cầm — sổ không dựng form riêng). */
  onCreatePrintOrder?: (orderId: string) => void;
};

export function AftercareSubTab({ onCreatePrintOrder }: Props) {
  const productLabel = useProductLabel();
  const branchLabel = useBranchLabel();
  const me = useAuthV2((s) => s.currentStaff);
  const canWrite = canWriteAftercare(me);
  const [filter, setFilter] = useState<AftercareFilterId>("chua_gui");
  const [page, setPage] = useState(1);
  const [touchTarget, setTouchTarget] = useState<AftercareItem | null>(null);
  // selected — các đơn được tick để ghi "đã chào" hàng loạt. Xoá sạch mỗi khi
  // đổi chip hoặc đổi trang: giữ lại là ghi nhầm cho khách đã trôi khỏi màn.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const toggleOne = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // limit/offset đưa thẳng: máy chủ đã chuẩn hoá (thiếu → 20, trần 200, offset
  // âm → 0). Chuẩn hoá lần nữa ở đây là dựng bộ luật thứ hai rồi sớm muộn lệch.
  const query = useAftercareList({
    filter,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const touch = useAftercareTouch();
  const sendEmail = useAftercareEmail();
  const close = useAftercareClose();
  const reopen = useAftercareReopen();

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;

  // Số đơn mỗi nhóm — đếm trên TRANG ĐANG XEM, đúng bằng số dòng nằm dưới dải
  // tiêu đề đó. Đếm trên `total` là hứa một con số mà cuộn xuống không thấy.
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const it of items) {
      const id = groupOf(it.daysSinceDelivered).id;
      m[id] = (m[id] ?? 0) + 1;
    }
    return m;
  }, [items]);

  const columns = useMemo<ColumnDef<AftercareItem>[]>(
    () => [
      {
        id: "select",
        header: () => <span className="sr-only">Chọn</span>,
        meta: { mobilePriority: "hidden" },
        cell: ({ row }) => {
          // Chỉ dòng ĐANG MỞ mới chọn được: hành động hàng loạt duy nhất là
          // ghi "đã chào", mà dòng đã đóng / đã mua thì không ghi được nữa.
          const open = row.original.status === "open";
          if (!open) return null;
          const id = row.original.orderId;
          return (
            <Checkbox
              checked={selected.has(id)}
              onCheckedChange={() => toggleOne(id)}
              aria-label={`Chọn ${row.original.customer}`}
            />
          );
        },
      },
      {
        id: "customer",
        header: "Khách",
        meta: { mobilePriority: "primary" },
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.customer}</div>
            <div className="text-xs text-muted-foreground">
              {row.original.phone}
            </div>
          </div>
        ),
      },
      {
        id: "product",
        header: "Loại đơn",
        meta: { mobileLabel: "Loại đơn" },
        // productLabel/branchLabel rơi về mã gốc khi không tra được, nên đơn
        // gói lạ vẫn hiện chữ chứ không thành ô trống.
        cell: ({ row }) =>
          productLabel(row.original.product, { full: true }) || row.original.category,
      },
      {
        id: "branch",
        header: "Cơ sở",
        meta: { mobileLabel: "Cơ sở" },
        cell: ({ row }) => branchLabel(row.original.branch),
      },
      {
        id: "value",
        header: () => <span className="block text-right">Giá trị đơn</span>,
        meta: { mobilePriority: "secondary", mobileLabel: "Giá trị đơn" },
        // Máy chủ xếp đơn to lên trước trong cùng nhóm quá hạn (luật 3c) — cột
        // này để người CSKH THẤY vì sao dòng đó đứng trên, chứ không phải đoán.
        cell: ({ row }) => (
          <div className="text-right text-sm font-medium tabular-nums">
            {formatVND(row.original.orderValue)}
          </div>
        ),
      },
      {
        id: "days",
        header: "Từ khi trả ảnh",
        meta: { mobileLabel: "Từ khi trả ảnh" },
        cell: ({ row }) => <DaysBadge days={row.original.daysSinceDelivered} />,
      },
      {
        id: "touches",
        header: "Đã chào",
        meta: { mobileLabel: "Đã chào" },
        // Ba chấm để liếc là biết còn mấy lần, kèm số để khỏi phải đếm chấm.
        cell: ({ row }) => <TouchDots count={row.original.touchCount} />,
      },
      {
        id: "actions",
        header: "",
        meta: { mobileLabel: "" },
        cell: ({ row }) => (
          <RowActions
            item={row.original}
            onTouch={() => setTouchTarget(row.original)}
            onEmail={async () => {
              try {
                await sendEmail.mutateAsync({ orderId: row.original.orderId });
                toast.success("Đã gửi thư cho khách");
              } catch (e) {
                // Giữ nguyên lời máy chủ: đó là thứ duy nhất nói vì sao nút
                // không ăn ("đơn này chưa có email khách", "khách đã mua thêm
                // rồi"). Bọc thêm một câu chung chung là nuốt mất lý do.
                toast.error(e instanceof Error ? e.message : "Gửi thư không thành công");
              }
            }}
            onClose={async (reason) => {
              try {
                await close.mutateAsync({ orderId: row.original.orderId, reason });
                toast.success("Đã đóng");
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Không đóng được");
              }
            }}
            onReopen={async () => {
              try {
                await reopen.mutateAsync({ orderId: row.original.orderId });
                toast.success("Đã mở lại");
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Không mở lại được");
              }
            }}
            {...(onCreatePrintOrder ? { onCreatePrintOrder } : {})}
          />
        ),
      },
    ],
    [branchLabel, close, onCreatePrintOrder, productLabel, reopen, sendEmail, selected],
  );

  if (!canWrite) {
    return (
      <div className="rounded-md border bg-surface p-6 text-center text-muted">
        Bạn không có quyền xem sổ hậu mãi.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {AFTERCARE_FILTERS.map((f) => (
          <Button
            key={f.id}
            size="sm"
            variant={filter === f.id ? "default" : "outline"}
            aria-pressed={filter === f.id}
            onClick={() => {
              setFilter(f.id);
              // Đứng ở trang 3 rồi đổi chip là rơi vào trang trống — nhóm mới
              // gần như luôn ít dòng hơn nhóm đang xem.
              setPage(1);
              setSelected(new Set());
            }}
          >
            {f.label}
          </Button>
        ))}
      </div>

      <HintNote>
        Màu vàng = quá 3 ngày kể từ khi trả ảnh (nên chào in ảnh) · màu đỏ = quá 7 ngày (nên gọi hậu
        mãi). Đây chỉ là gợi ý sắp xếp, máy không tự nhắn cho khách.
      </HintNote>

      {query.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : query.isError ? (
        // Bảng rỗng câm khi tải hỏng là nói dối "hôm nay không có đơn nào".
        <div className="rounded-md border border-red-200 bg-red-50 p-6 text-center text-sm text-red-800">
          Không tải được sổ hậu mãi. Kiểm tra đường mạng rồi mở lại trang.
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-md border bg-surface p-6 text-center text-sm text-muted-foreground">
          Không có đơn nào trong nhóm này
        </div>
      ) : (
        /* MỘT bảng, chia nhóm bằng dải tiêu đề chèn giữa các dòng. Máy chủ ĐÃ
           trả đúng thứ tự (quá hạn trước, trong nhóm thì đơn to trước) nên
           dòng cùng nhóm vốn đã nằm liền nhau — chỉ cần đánh dấu chỗ sang
           nhóm, KHÔNG sắp lại: sắp lại một trang 20 dòng là sắp trong cái đã
           bị cắt sẵn.

           Trước đây mỗi nhóm một `DataTable` riêng. Mỗi bảng tự tính độ rộng
           cột theo dữ liệu của chính nó nên ba khối xếp chồng bị lệch cột (đo
           01/09/2026: "Loại đơn" lệch 36px, "Cơ sở" 30px), và hàng tiêu đề cột
           bị lặp ba lần. */
        <DataTable
          columns={columns}
          data={items}
          mobileVariant="stack"
          groupBy={(it) => {
            const g = groupOf(it.daysSinceDelivered);
            return {
              id: g.id,
              header: (
                <span className="flex flex-wrap items-baseline gap-x-2">
                  {/* Màu chỉ TÔ ĐẬM thêm cho chữ đã nói rõ ("QUÁ 7 NGÀY"),
                      không phải thứ duy nhất mang nghĩa — người khó phân biệt
                      màu vẫn đọc được nhóm. */}
                  <strong className={cn("font-bold uppercase tracking-wide", g.tone)}>
                    {g.label}
                  </strong>
                  <span>
                    {g.hint} · {counts[g.id]} đơn
                  </span>
                </span>
              ),
            };
          }}
          emptyMessage="Không có đơn nào trong nhóm này"
        />
      )}

      {/* Thanh hành động hàng loạt — chỉ hiện khi đã tick ít nhất một dòng. */}
      {selected.size > 0 && (
        <div className="sticky bottom-2 z-10 flex flex-wrap items-center gap-2 rounded-md border bg-surface p-2 shadow-lg">
          <span className="text-sm">
            Đã chọn <strong className="tabular-nums">{selected.size}</strong> khách
          </span>
          <Button size="sm" onClick={() => setBulkOpen(true)}>
            Ghi đã chào cho {selected.size} khách
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
            Bỏ chọn
          </Button>
        </div>
      )}

      <Pagination
        total={total}
        pageSize={PAGE_SIZE}
        current={page}
        onPageChange={(p) => {
          setPage(p);
          setSelected(new Set());
        }}
        itemLabel="đơn"
      />

      {/* key theo đơn: hộp thoại giữ nguyên hooks khi đóng, không thay key là
          ghi chú gõ cho khách trước dính sang khách sau. */}
      <TouchDialog
        key={touchTarget?.orderId ?? "none"}
        item={touchTarget}
        pending={touch.isPending}
        onCancel={() => setTouchTarget(null)}
        onSubmit={async (channel, note) => {
          if (!touchTarget) return;
          try {
            await touch.mutateAsync({
              orderId: touchTarget.orderId,
              channel,
              ...(note ? { note } : {}),
            });
            toast.success(`Đã ghi lần ${touchTarget.touchCount + 1}`);
            setTouchTarget(null);
          } catch (e) {
            toast.error(e instanceof Error ? e.message : "Không ghi được");
          }
        }}
      />

      {/* Ghi "đã chào" cho nhiều khách một lượt. Máy chủ chỉ có cửa ghi TỪNG
          đơn, nên đây là vòng lặp gọi lần lượt — và vì vậy phải báo lại số
          thành công / thất bại thật, không được báo "xong" khi mới xong một nửa. */}
      <BulkTouchDialog
        open={bulkOpen}
        count={selected.size}
        pending={touch.isPending}
        onCancel={() => setBulkOpen(false)}
        onSubmit={async (channel, note) => {
          const ids = [...selected];
          let ok = 0;
          const failed: string[] = [];
          for (const orderId of ids) {
            try {
              await touch.mutateAsync({ orderId, channel, ...(note ? { note } : {}) });
              ok++;
            } catch (e) {
              failed.push(e instanceof Error ? e.message : "lỗi không rõ");
            }
          }
          setBulkOpen(false);
          setSelected(new Set());
          if (failed.length === 0) {
            toast.success(`Đã ghi lần chào cho ${ok} khách`);
          } else {
            // Nói đúng con số: "xong" khi 3/10 dòng hỏng là báo cáo sai.
            toast.error(
              `Ghi được ${ok}/${ids.length} khách. ${failed.length} đơn không ghi được — ${failed[0]}`,
            );
          }
        }}
      />
    </div>
  );
}

/**
 * TouchDots — số lần đã chào dưới dạng 3 chấm (tối đa 3 lần theo spec §6.4),
 * kèm số "N/3" viết ra.
 *
 * Ba chấm để liếc là biết còn mấy lần; con số để KHÔNG phải đếm chấm. Bỏ số đi
 * thì trên điện thoại chỉ còn ba vòng tròn 8px, phải nhìn kỹ mới phân biệt
 * được chấm đặc với chấm rỗng.
 */
function TouchDots({ count }: { count: number }) {
  return (
    <span
      className="inline-flex items-center gap-1"
      title={`Đã chào ${count}/3 lần`}
      aria-label={`Đã chào ${count} trên 3 lần`}
    >
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          aria-hidden
          className={`inline-block size-2 rounded-full ${
            i < count ? "bg-foreground" : "border border-muted-foreground/40"
          }`}
        />
      ))}
      <span aria-hidden className="ml-0.5 text-xs tabular-nums text-muted-foreground">
        {count}/3
      </span>
    </span>
  );
}

/** Màu theo mốc 3 / 7 ngày của spec §6.4 — CHỈ là màu, không có thông báo nào. */
function DaysBadge({ days }: { days: number }) {
  const tone =
    days >= 7 ? "bg-red-100 text-red-800" : days >= 3 ? "bg-amber-100 text-amber-900" : "";
  return <span className={`rounded px-1.5 py-0.5 ${tone}`}>{days} ngày</span>;
}

type RowActionProps = {
  item: AftercareItem;
  onTouch: () => void;
  onEmail: () => void;
  onClose: (reason: AftercareCloseReasonId) => void;
  onReopen: () => void;
  onCreatePrintOrder?: (orderId: string) => void;
};

function RowActions({
  item,
  onTouch,
  onEmail,
  onClose,
  onReopen,
  onCreatePrintOrder,
}: RowActionProps) {
  // Khách đã mua thêm thì thôi chào: máy tự đặt trạng thái này khi đơn in được
  // tạo, giữ nút lại chỉ tổ để CSKH chào người vừa trả tiền.
  if (item.status === "purchased") return <Badge>Đã mua thêm</Badge>;
  if (item.status === "closed") {
    return (
      <div className="flex flex-wrap gap-1">
        <Badge variant="outline">{closedLabel(item.closedReason)}</Badge>
        <Button size="sm" variant="outline" onClick={onReopen}>
          Mở lại
        </Button>
      </div>
    );
  }
  // MỘT nút chính, phần còn lại gom vào menu.
  //
  // Bản trước xếp 5 nút cạnh nhau trong một ô bảng, hai trong số đó dạng chữ
  // trơn (variant ghost) nên đọc y như nhãn chứ không như nút bấm — và hai nút
  // đó lại là hai nút ĐÓNG SỔ, tức việc không lùi được. Việc làm hằng ngày là
  // "ghi đã chào"; mọi việc khác thưa hơn hẳn nên vào menu.
  return (
    <div className="flex items-center justify-end gap-1">
      {/* Nhãn là VIỆC SẮP LÀM, không phải tình trạng. Bản trước ghi "Đã chào
          lần {touchCount + 1}" nên nút đứng ngay cạnh cột tình trạng mà nói
          một con số KHÁC nó: khách chưa chào lần nào (0/3) thì nút vẫn ghi
          "Đã chào lần 1" — đọc thành "đã chào rồi". Số lần cụ thể nằm trong
          hộp thoại xác nhận, chỗ mà nó là một lời khẳng định thật. */}
      <Button size="sm" onClick={onTouch}>
        Ghi đã chào
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="icon" variant="ghost" aria-label={`Thao tác khác cho ${item.customer}`}>
            <MoreHorizontal className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {/* Đơn không có địa chỉ thư của khách: máy chủ trả 400, nên mục phải
              biến mất chứ không phải hiện rồi báo lỗi sau khi bấm. */}
          {item.hasEmail && <DropdownMenuItem onClick={onEmail}>Gửi email</DropdownMenuItem>}
          {onCreatePrintOrder && (
            <DropdownMenuItem onClick={() => onCreatePrintOrder(item.orderId)}>
              Tạo đơn in
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {AFTERCARE_CLOSE_REASONS.map((r) => (
            <DropdownMenuItem key={r.id} onClick={() => onClose(r.id)}>
              Đóng sổ: {r.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function BulkTouchDialog({
  open,
  count,
  pending,
  onCancel,
  onSubmit,
}: {
  open: boolean;
  count: number;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (channel: AftercareChannelId, note: string) => void;
}) {
  const [channel, setChannel] = useState<AftercareChannelId>("zalo");
  const [note, setNote] = useState("");
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Ghi đã chào cho {count} khách</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Ghi vào sổ rằng đã chào {count} khách này. Máy KHÔNG tự nhắn tin cho khách — đây chỉ là
          ghi lại việc bạn vừa làm.
        </p>
        <div className="flex flex-wrap gap-2">
          {AFTERCARE_CHANNELS.map((c) => (
            <Button
              key={c.id}
              size="sm"
              variant={channel === c.id ? "default" : "outline"}
              aria-pressed={channel === c.id}
              onClick={() => setChannel(c.id)}
            >
              {c.label}
            </Button>
          ))}
        </div>
        <Textarea
          value={note}
          maxLength={500}
          placeholder="Khách phản hồi gì — ghi chung cho cả nhóm"
          onChange={(e) => setNote(e.target.value)}
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>
            Huỷ
          </Button>
          <Button disabled={pending} onClick={() => onSubmit(channel, note.trim())}>
            Ghi cho {count} khách
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const CLOSE_LABEL = new Map<string, string>(AFTERCARE_CLOSE_REASONS.map((r) => [r.id, r.label]));

function closedLabel(reason: string): string {
  // `da_chao_du` là lý do MÁY đặt khi chạm trần 3 lần chào nên không nằm trong
  // danh sách người dùng chọn được — thiếu nhánh này là hiện mã máy ra màn.
  if (reason === "da_chao_du") return "Đã chào đủ 3 lần";
  return CLOSE_LABEL.get(reason) ?? "Đã đóng";
}

function TouchDialog({
  item,
  pending,
  onCancel,
  onSubmit,
}: {
  item: AftercareItem | null;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (channel: AftercareChannelId, note: string) => void;
}) {
  const [channel, setChannel] = useState<AftercareChannelId>("zalo");
  const [note, setNote] = useState("");
  if (!item) return null;
  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            Đã gửi thông tin lần {item.touchCount + 1} — {item.customer}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {AFTERCARE_CHANNELS.map((c) => (
            <Button
              key={c.id}
              size="sm"
              variant={channel === c.id ? "default" : "outline"}
              aria-pressed={channel === c.id}
              onClick={() => setChannel(c.id)}
            >
              {c.label}
            </Button>
          ))}
        </div>
        <Textarea
          value={note}
          maxLength={500}
          placeholder="Khách phản hồi gì — ví dụ: khách hẹn xem lại cuối tuần"
          onChange={(e) => setNote(e.target.value)}
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel}>
            Huỷ
          </Button>
          <Button disabled={pending} onClick={() => onSubmit(channel, note.trim())}>
            Ghi nhận
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
